import { userQueryKeys } from '@/config/queryKeys';
import { isSelfModification, isUserSuperAdminInOrg } from '@/config/users';
import { grantUserSuperAdmin, revokeUserSuperAdmin } from '@/services/users';
import { Role, UserPublic } from '@/types';
import Button from '@nautilusbio/nb-design-system/Button';
import Group from '@nautilusbio/nb-design-system/Group';
import { getToastClassNames } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Switch } from '@nautilusbio/nb-design-system/Switch';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface SuperAdminToggleCellProps {
  user: UserPublic;
  orgId: string;
  currentUserId: string;
  /**
   * Optional override for the SuperAdmin state. When provided, this value is used
   * directly instead of computing it from `isUserSuperAdminInOrg(user, orgId)`.
   *
   * Use this when the data source (e.g. POST /users/search) returns the platform-level
   * `user.is_super_admin` flag but does not populate org-scoped `allowed_roles`.
   */
  isSuperAdminOverride?: boolean;
  /**
   * Optional callback invoked after the grant/revoke mutation settles (success or error).
   *
   * Use this to trigger a server-side table refresh from the parent component — e.g.
   * calling `gridRef.current?.api?.refreshServerSide()` — since the AG Grid server-side
   * datasource bypasses React Query and won't react to `queryClient.invalidateQueries`.
   */
  onMutationSettled?: () => void;
}

/**
 * Cell renderer for the SuperAdmin toggle column in the Users table.
 *
 * Only rendered when the current authenticated user is a global SuperAdmin.
 * Displays a Switch reflecting the user's current SuperAdmin status in the org,
 * and shows a confirmation toast before applying any changes.
 *
 * Self-modification is prevented: the toggle is disabled for the current user's own row.
 */
const SuperAdminToggleCell: FC<SuperAdminToggleCellProps> = ({
  user,
  orgId,
  currentUserId,
  isSuperAdminOverride,
  onMutationSettled,
}) => {
  const { t } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');
  const queryClient = useQueryClient();

  const isSuperAdmin = isSuperAdminOverride ?? isUserSuperAdminInOrg(user, orgId);
  const isSelf = isSelfModification(currentUserId, user.id);

  const toggleMutation = useMutation({
    mutationFn: async () => {
      if (isSuperAdmin) {
        return revokeUserSuperAdmin(user.id);
      }
      return grantUserSuperAdmin(user.id);
    },
    onMutate: async () => {
      // Optimistic update — flip is_super_admin and the org-scoped SuperAdmin role
      // immediately in both the org users cache and any cached UserPublic entries.
      await queryClient.cancelQueries({ queryKey: userQueryKeys.orgUsers(orgId) });
      const previousUsers = queryClient.getQueryData<UserPublic[]>(userQueryKeys.orgUsers(orgId));

      queryClient.setQueryData<UserPublic[]>(
        userQueryKeys.orgUsers(orgId),
        old =>
          old?.map(u => {
            if (u.id !== user.id) return u;
            const currentRoles: Role[] = u.allowed_roles?.[orgId] ?? [];
            const updatedRoles: Role[] = isSuperAdmin
              ? currentRoles.filter(r => r !== 'super_user')
              : ([...currentRoles, 'super_user'] as Role[]);
            return {
              ...u,
              is_super_admin: !isSuperAdmin,
              allowed_roles: {
                ...u.allowed_roles,
                [orgId]: updatedRoles,
              },
            };
          }) ?? [],
      );

      return { previousUsers };
    },
    onError: (_err, _vars, context) => {
      // Roll back on failure
      if (context?.previousUsers) {
        queryClient.setQueryData(userQueryKeys.orgUsers(orgId), context.previousUsers);
      }
    },
    onSettled: () => {
      // Re-sync the org-scoped users list and any other user queries (e.g. global search).
      // SuperAdmin is now a platform-level flag so we invalidate broadly.
      queryClient.invalidateQueries({ queryKey: userQueryKeys.orgUsers(orgId) });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
      // Notify the parent so it can refresh an AG Grid server-side datasource if needed.
      // The server-side datasource makes its own HTTP calls and won't react to query
      // cache invalidation, so the parent must call refreshServerSide() explicitly.
      onMutationSettled?.();
    },
  });

  const handleTogglePress = useCallback(() => {
    if (isSelf || toggleMutation.isPending) return;

    const title = isSuperAdmin
      ? t('revoke_super_admin_title', { name: user.full_name })
      : t('grant_super_admin_title', { name: user.full_name });

    const description = isSuperAdmin
      ? t('revoke_super_admin_description', { name: user.full_name })
      : t('grant_super_admin_description', { name: user.full_name });

    const confirmVisual = isSuperAdmin ? 'danger' : 'primary';
    const loadingKey = isSuperAdmin ? 'revoke_super_admin_loading' : 'grant_super_admin_loading';
    const successKey = isSuperAdmin ? 'revoke_super_admin_success' : 'grant_super_admin_success';
    const errorKey = isSuperAdmin ? 'revoke_super_admin_error' : 'grant_super_admin_error';

    toast.custom(
      toastId => (
        <Stack className={getToastClassNames('default')} gap={3} orientation="vertical">
          <Stack fullWidth orientation="vertical">
            <Text as="h3">{title}</Text>
            <Text as="p">{description}</Text>
          </Stack>

          <Group fullWidth justifyContent="end" orientation="horizontal" size="md">
            <Button
              data-testid={`super-admin-cancel-${user.id}`}
              onPress={() => toast.dismiss(toastId)}
              visual="default"
            >
              {tGeneral('cancel')}
            </Button>

            <Button
              data-testid={`super-admin-confirm-${user.id}`}
              onPress={async () => {
                toast.dismiss(toastId);
                const loadingToastId = toast.loading(t(loadingKey, { name: user.full_name }));
                try {
                  await toggleMutation.mutateAsync();
                  toast.success(t(successKey, { name: user.full_name }), { id: loadingToastId });
                } catch {
                  toast.error(t(errorKey, { name: user.full_name }), { id: loadingToastId });
                }
              }}
              visual={confirmVisual}
            >
              {tGeneral('confirm')}
            </Button>
          </Group>
        </Stack>
      ),
      { duration: Infinity },
    );
  }, [isSelf, isSuperAdmin, t, tGeneral, toggleMutation, user.full_name, user.id]);

  const tooltipContent = isSelf
    ? t('super_admin_self_tooltip')
    : isSuperAdmin
      ? t('revoke_super_admin_tooltip')
      : t('grant_super_admin_tooltip');

  return (
    <Tooltip
      content={tooltipContent}
      placement="left"
      trigger={
        <Switch
          checked={isSuperAdmin}
          data-testid={`super-admin-toggle-${user.id}`}
          disabled={isSelf || toggleMutation.isPending}
          onChange={handleTogglePress}
          size="sm"
        />
      }
    />
  );
};

export default memo(SuperAdminToggleCell);
